import React, { useEffect, useMemo, useState } from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import RoundedPaper from 'shared/components/RoundedPaper';
import SearchTermReviewService from 'services/SearchTermReviewService';
import SettingsService from 'services/SettingsService';
import FlowingProgress from 'pages/SearchTermReview/components/FlowingProgress';
import SearchTermTable, { groupPlurals } from 'pages/SearchTermReview/components/SearchTermTable';
import {
  Campaign,
  DecisionRecord,
  PushResponse,
  ReviewResult,
  ReviewRow,
  RetryJudgment,
  RunListItem,
  Section,
} from 'pages/SearchTermReview/types';

const service = new SearchTermReviewService();
const settingsService = new SettingsService();

const REVIEWER_KEY = 'sbly-tools:search-term-reviewer';
const SETTINGS_NS = 'search-term-review';
const AI_BATCH = 20; // placements per /retry call
// Every actionable row is AI-scored now, so a wide audit sends hundreds of batches —
// run them through a small pool instead of all at once (server + LLM rate limits).
const AI_CONCURRENCY = 6;
const STORES = ['US', 'CA', 'AU', 'UK', 'ALL'];

// Batch rows for the AI pass WITHOUT splitting a search term across batches — the
// backend judges all of a term's placements (ad groups) as one unit, so they must
// travel in the same request. A term with more placements than `size` gets its own
// oversize batch.
function chunkByTerm(rows: ReviewRow[], size: number): ReviewRow[][] {
  const groups = new Map<string, ReviewRow[]>();
  for (const r of rows) {
    const key = `${r.store} ${r.search_term}`;
    const g = groups.get(key);
    if (g) g.push(r);
    else groups.set(key, [r]);
  }
  const batches: ReviewRow[][] = [];
  let current: ReviewRow[] = [];
  for (const group of groups.values()) {
    if (current.length > 0 && current.length + group.length > size) {
      batches.push(current);
      current = [];
    }
    current.push(...group);
  }
  if (current.length) batches.push(current);
  return batches;
}

// Survives route changes within the SPA (this page unmounts when you switch to
// another tool) — coming back restores the audit you were on instantly. A full page
// reload starts empty; the persisted audit history covers that case.
interface SessionCache {
  result: ReviewResult;
  monitorRows: ReviewRow[];
  scope: { store: string; selectedCampaigns: Campaign[]; days: number; spendFloor: number; lagDays: number };
  // term_keys already pushed to Google Ads this session — so a row isn't offered for
  // push again (and stays marked) after switching tools and coming back.
  pushedKeys: Set<string>;
}
let sessionCache: SessionCache | null = null;

const SECTION_ORDER: Section[] = [
  'negatives',
  'add-keyword',
  'refine-match-type',
  'funnel-review',
  'monitor',
];
const SECTION_LABELS: Record<Section, string> = {
  negatives: 'Negatives',
  'add-keyword': 'Add Keyword',
  'refine-match-type': 'Refine Match Type',
  'funnel-review': 'Funnel Review',
  monitor: 'Monitor',
};

// Category filter over the row flags (the chips on each row).
type FlagFilter = 'all' | 'comp' | 'brand' | 'waste' | 'negated' | 'notadded' | 'added';
const FLAG_LABELS: Record<FlagFilter, string> = {
  all: 'All',
  comp: 'Comp',
  brand: 'Brand',
  waste: 'Waste',
  negated: 'Already negated',
  notadded: 'Not added',
  added: 'Already added',
};
const flagMatches = (row: ReviewRow, f: FlagFilter): boolean => {
  if (f === 'all') return true;
  if (f === 'comp') return row.competitor_flag;
  if (f === 'brand') return row.own_brand_flag;
  if (f === 'waste') return row.waste_flag;
  if (f === 'negated') return Boolean(row.already_negated);
  if (f === 'notadded') return !row.already_added;
  return Boolean(row.already_added);
};

// Backend timestamps are UTC but may arrive ISO ("…Z") or Snowflake-naive
// ("YYYY-MM-DD HH:MM:SS.mmm") depending on the driver's row shape — normalize both.
function formatRunTime(ts: string): string {
  const iso = /[zZ]|[+-]\d\d:\d\d$/.test(ts) ? ts : `${ts.replace(' ', 'T')}Z`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function SearchTermReview() {
  const [store, setStore] = useState(() => sessionCache?.scope.store ?? 'US');
  // Campaign pick-list for the audit scope: the market's campaigns (fetched per store)
  // and the subset the user chose. An empty selection audits the whole market.
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Campaign[]>(
    () => sessionCache?.scope.selectedCampaigns ?? [],
  );
  const [days, setDays] = useState(() => sessionCache?.scope.days ?? 30);
  const [spendFloor, setSpendFloor] = useState(() => sessionCache?.scope.spendFloor ?? 1);
  // Conversion-lag buffer: the window ends N days before today. Google attributes
  // conversions to the click date retroactively, so the most recent days always
  // undercount conversions until later daily syncs restate them.
  const [lagDays, setLagDays] = useState(() => sessionCache?.scope.lagDays ?? 2);

  const [result, setResult] = useState<ReviewResult | null>(() => sessionCache?.result ?? null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  // AI-scoring progress (actionable rows are scored client-side, through a pool).
  const [aiProgress, setAiProgress] = useState<{ done: number; total: number; campaigns: string[] } | null>(null);
  // Rows whose scoring request failed even after the automatic retry pass — surfaced
  // with a retry button instead of silently staying unclassified.
  const [aiFailedRows, setAiFailedRows] = useState<ReviewRow[]>([]);
  // The low-value "monitor" tail is lazy-loaded, kept separate from the primary rows.
  const [monitorRows, setMonitorRows] = useState<ReviewRow[]>(() => sessionCache?.monitorRows ?? []);
  const [monitorLoading, setMonitorLoading] = useState(false);

  // Push-to-Google-Ads flow: confirm dialog -> pushing -> results summary.
  const [pushConfirmOpen, setPushConfirmOpen] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [pushResponse, setPushResponse] = useState<PushResponse | null>(null);
  // term_keys already applied to Google Ads this session — dropped from the pushable
  // set and marked in the table so an approved row can't be pushed twice.
  const [pushedKeys, setPushedKeys] = useState<Set<string>>(() => sessionCache?.pushedKeys ?? new Set());

  // Persisted audit history (the log of past runs, reopenable).
  const [runs, setRuns] = useState<RunListItem[]>([]);
  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);

  // Client-side table filters. Campaign filter is multi-select: a row matches if its
  // campaign name contains ANY of the chosen fragments (empty = no campaign filter).
  const [filterCampaigns, setFilterCampaigns] = useState<string[]>([]);
  const [filterMinSpend, setFilterMinSpend] = useState('');
  const [filterMaxSpend, setFilterMaxSpend] = useState('');
  const [filterMinRoas, setFilterMinRoas] = useState('');
  const [filterMaxRoas, setFilterMaxRoas] = useState('');
  const [aiFilter, setAiFilter] = useState<'all' | 'ai' | 'rules'>('all');
  const [flagFilter, setFlagFilter] = useState<FlagFilter>('all');
  const [activeTab, setActiveTab] = useState<Section>('negatives');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [reviewerName, setReviewerName] = useState(
    () => localStorage.getItem(REVIEWER_KEY) ?? '',
  );

  // Negatives require ROAS below this (true waste = unprofitable AND intent mismatch).
  const [negRoasThreshold, setNegRoasThreshold] = useState(1.5);
  // Google Ads "obfuscated customer id" — the number after ocid= in any ads.google.com
  // URL while in the account. Makes the per-row deep links open the right account.
  const [googleAdsOcid, setGoogleAdsOcid] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  useEffect(() => {
    settingsService.get(SETTINGS_NS).then(s => {
      if (!s) return;
      if (typeof s.negativesRoasThreshold === 'number') setNegRoasThreshold(s.negativesRoasThreshold);
      if (typeof s.googleAdsOcid === 'string') setGoogleAdsOcid(s.googleAdsOcid);
    });
    refreshRuns();
    // A cached audit restored mid-AI-scoring has rows still marked pending (the
    // in-flight scoring died with the unmounted page) — pick the scoring back up.
    if (sessionCache?.result?.rows.some(r => r.ai_pending)) {
      scoreAiPending(sessionCache.result.rows, sessionCache.result.header.runId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the module-level cache current so switching tools and back restores this page.
  useEffect(() => {
    if (result) {
      sessionCache = { result, monitorRows, scope: { store, selectedCampaigns, days, spendFloor, lagDays }, pushedKeys };
    }
  }, [result, monitorRows, store, selectedCampaigns, days, spendFloor, lagDays, pushedKeys]);

  // Load the market's campaigns for the pick-list whenever the store changes. The
  // in-flight guard drops a stale response if the user flips markets quickly.
  useEffect(() => {
    let cancelled = false;
    setCampaignsLoading(true);
    service.listCampaigns(store).then(res => {
      if (cancelled) return;
      setCampaigns(res?.campaigns ?? []);
      setCampaignsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [store]);

  const refreshRuns = () => {
    service.listRuns().then(res => {
      if (res?.runs) setRuns(res.runs);
    });
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    setSettingsSaved(false);
    const saved = await settingsService.update(SETTINGS_NS, {
      negativesRoasThreshold: Math.max(0, Number(negRoasThreshold) || 1.5),
      googleAdsOcid: googleAdsOcid.trim(),
    });
    if (saved) {
      if (typeof saved.negativesRoasThreshold === 'number') setNegRoasThreshold(saved.negativesRoasThreshold);
      if (typeof saved.googleAdsOcid === 'string') setGoogleAdsOcid(saved.googleAdsOcid);
      setSettingsSaved(true);
    }
    setSavingSettings(false);
  };

  const onReviewerChange = (name: string) => {
    setReviewerName(name);
    localStorage.setItem(REVIEWER_KEY, name);
  };

  const scopeParams = () => ({
    store,
    campaignIds: selectedCampaigns.map(c => c.id),
    days,
    spendFloor,
    lagDays,
  });
  const scopeFromResult = (r: ReviewResult) => ({
    store: r.header.store,
    campaignIds: r.header.campaignIds ?? [],
    days: r.header.days,
    spendFloor: r.header.spendFloor,
    lagDays: r.header.lagDays ?? r.summary.lagDays ?? 2,
  });

  const runAudit = async () => {
    setLoading(true);
    setError(null);
    setMonitorRows([]);
    setAiProgress(null);
    const { data, error: runError } = await service.runReview(scopeParams());
    setLoading(false);
    if (!data) {
      setResult(null);
      setError(runError || 'The audit request failed.');
      return;
    }
    setResult(data);
    refreshRuns(); // the run was just persisted to the history log
    // Deterministic rows are on screen now; AI-score the top-spend rows in parallel,
    // updating the progress bar and the table live as each batch lands.
    scoreAiPending(data.rows, data.header.runId);
  };

  // Reopen a persisted audit from the history log. Stored AI judgments and current
  // decisions come pre-merged; any rows never scored resume scoring now.
  const loadRun = async (runId: string) => {
    setLoadingRunId(runId);
    setError(null);
    setMonitorRows([]);
    setAiProgress(null);
    const data = await service.getRun(runId);
    setLoadingRunId(null);
    if (!data) {
      setError('Could not load the saved audit.');
      return;
    }
    setStore(data.header.store);
    // The reopened run's rows already reflect its scope; reset the campaign pick-list
    // (its options reload for the run's market via the store effect).
    setSelectedCampaigns([]);
    setDays(data.header.days);
    setSpendFloor(data.header.spendFloor);
    setLagDays(data.header.lagDays ?? data.summary.lagDays ?? 2);
    setResult(data);
    scoreAiPending(data.rows, data.header.runId);
  };

  // decisions is the server's re-check of each row's approval against the AI-judged change.
  const applyJudgments = (judgments: RetryJudgment[], decisions: Record<string, DecisionRecord | null>) => {
    const byKey = new Map(judgments.map(j => [j.term_key, j]));
    setResult(prev =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map(r => {
              const j = byKey.get(r.term_key);
              return j
                ? {
                    ...r,
                    intent: j.intent,
                    relevance: j.relevance,
                    issue_category: j.issue_category,
                    keyword_intent_match: j.keyword_intent_match ?? null,
                    recommendation: j.recommendation,
                    reasoning_main: j.reasoning_main ?? j.reasoning,
                    reasoning_details: j.reasoning_details ?? [],
                    reasoning: j.reasoning,
                    negative_match_type: j.negative_match_type ?? null,
                    negative_keyword: j.negative_keyword ?? null,
                    section: j.section,
                    evaluated_by: j.evaluated_by,
                    ai_pending: false,
                    decision: decisions[r.term_key] ?? null,
                  }
                : r;
            }),
          }
        : prev,
    );
  };

  const scoreAiPending = async (rows: ReviewRow[], runId?: string) => {
    const pending = rows.filter(r => r.ai_pending);
    if (pending.length === 0) return;
    setAiFailedRows([]);
    const campaigns = Array.from(new Set(pending.map(r => r.campaign_name)));
    setAiProgress({ done: 0, total: pending.length, campaigns });

    // Small worker pool: every actionable row is scored now, so a wide audit can be
    // hundreds of batches — don't fire them all at once. Returns the failed batches.
    const runPool = async (batches: ReviewRow[][], countProgress: boolean): Promise<ReviewRow[][]> => {
      const failed: ReviewRow[][] = [];
      let next = 0;
      const worker = async () => {
        while (next < batches.length) {
          const batch = batches[next++];
          const res = await service.retryAi(batch, runId);
          if (res?.judgments) applyJudgments(res.judgments, res.decisions);
          else failed.push(batch);
          if (countProgress) setAiProgress(p => (p ? { ...p, done: p.done + batch.length } : p));
        }
      };
      await Promise.all(Array.from({ length: Math.min(AI_CONCURRENCY, batches.length) }, worker));
      return failed;
    };

    let failed = await runPool(chunkByTerm(pending, AI_BATCH), true);
    // One automatic retry pass covers transient failures (server hiccup, brief
    // network drop); whatever still fails gets a visible retry button.
    if (failed.length > 0) failed = await runPool(failed, false);
    setAiFailedRows(failed.flat());
    // Leave the panel showing 100% complete; the user dismisses it with "Hide".
  };

  const loadMoreMonitor = async () => {
    if (!result) return;
    setMonitorLoading(true);
    const nextPage = Math.floor(monitorRows.length / result.monitorPageSize);
    const res = await service.loadMonitor(scopeFromResult(result), nextPage);
    if (res?.rows) setMonitorRows(prev => [...prev, ...res.rows]);
    setMonitorLoading(false);
  };

  const saveOne = (row: ReviewRow, decision: 'approve' | 'deny') => {
    const base = {
      store: row.store,
      campaign_id: row.campaign_id,
      ad_group_id: row.ad_group_id,
      search_term: row.search_term,
      triggering_keyword: row.triggering_keyword,
      match_type: row.match_type,
      reviewer: reviewerName.trim(),
      recommendation_type: row.recommendation,
    };
    return service.saveDecision(decision === 'approve' ? { ...base, decision, row } : { ...base, decision });
  };

  // Merge a map of term_key -> decision into both the primary rows and the loaded
  // monitor tail (a bulk action can span either).
  const applyDecisions = (byKey: Map<string, DecisionRecord | null>) => {
    const patch = (list: ReviewRow[]) =>
      list.map(r => (byKey.has(r.term_key) ? { ...r, decision: byKey.get(r.term_key) ?? null } : r));
    setResult(prev => (prev ? { ...prev, rows: patch(prev.rows) } : prev));
    setMonitorRows(prev => patch(prev));
  };

  const toRecord = (saved: { termKey: string } & DecisionRecord): DecisionRecord => ({
    decision: saved.decision,
    reviewer: saved.reviewer,
    role: saved.role,
    recommendation_type: saved.recommendation_type,
    ts: saved.ts,
    approved_change: saved.approved_change,
    pushed: saved.pushed,
  });

  const handleDecision = async (row: ReviewRow, decision: 'approve' | 'deny') => {
    if (reviewerName.trim() === '') return;
    setSavingKey(row.term_key);
    // Clicking the already-chosen option again toggles it off — recorded as its own
    // 'clear' decision in the audit log.
    if (row.decision?.decision === decision) {
      const cleared = await service.clearDecision({
        store: row.store,
        campaign_id: row.campaign_id,
        ad_group_id: row.ad_group_id,
        search_term: row.search_term,
        triggering_keyword: row.triggering_keyword,
        match_type: row.match_type,
        reviewer: reviewerName.trim(),
        recommendation_type: row.recommendation,
      });
      if (cleared) applyDecisions(new Map([[row.term_key, null]]));
    } else {
      const saved = await saveOne(row, decision);
      if (saved) applyDecisions(new Map([[row.term_key, toRecord(saved)]]));
    }
    setSavingKey(null);
  };

  const handleBulkDecision = async (rowsToActOn: ReviewRow[], decision: 'approve' | 'deny') => {
    if (reviewerName.trim() === '' || rowsToActOn.length === 0) return;
    setBulkSaving(true);
    const saved = await Promise.all(rowsToActOn.map(r => saveOne(r, decision)));
    const byKey = new Map<string, DecisionRecord>();
    rowsToActOn.forEach((r, i) => {
      const s = saved[i];
      if (s) byKey.set(r.term_key, toRecord(s));
    });
    applyDecisions(byKey);
    setBulkSaving(false);
  };

  const fallbackRows = result ? result.rows.filter(r => r.evaluated_by === 'fallback') : [];

  const retryAi = async () => {
    if (!result || fallbackRows.length === 0) return;
    // Mark the rows as being scored, which locks their approve/deny buttons: the response
    // replaces each row's decision, so a decision saved mid-retry would be overwritten.
    const keys = new Set(fallbackRows.map(r => r.term_key));
    const setPending = (pending: boolean) =>
      setResult(prev =>
        prev
          ? { ...prev, rows: prev.rows.map(r => (keys.has(r.term_key) ? { ...r, ai_pending: pending } : r)) }
          : prev,
      );
    setRetrying(true);
    setPending(true);
    const res = await service.retryAi(fallbackRows, result.header.runId);
    if (res?.judgments) applyJudgments(res.judgments, res.decisions);
    setPending(false);
    setRetrying(false);
  };

  // Rows that can be pushed: line-by-line APPROVED, in an actionable section, and not
  // already pushed this session. (Monitor / Funnel Review approvals have no Google Ads
  // action; already-pushed rows must not be applied twice.)
  const PUSHABLE_SECTIONS: Section[] = ['negatives', 'add-keyword'];
  const pushableRows = useMemo(() => {
    const all = [...(result?.rows ?? []), ...monitorRows];
    return all.filter(
      r =>
        r.decision?.decision === 'approve' &&
        !r.decision.pushed &&
        PUSHABLE_SECTIONS.includes(r.section) &&
        !pushedKeys.has(r.term_key),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, monitorRows, pushedKeys]);

  const doPush = async () => {
    setPushConfirmOpen(false);
    setPushing(true);
    setError(null);
    const { data, error: pushError } = await service.pushToGoogleAds(pushableRows, reviewerName.trim());
    setPushing(false);
    if (!data) {
      setError(pushError || 'The push request failed.');
      return;
    }
    setPushResponse(data);
    // Mark the source rows of every change that actually took effect (applied in prod,
    // or validated in a dry-run env) so they drop out of the pushable set and can't be
    // re-applied. Failed / not-approved rows stay pushable so they can be retried.
    const done = new Set<string>();
    for (const res of data.results) {
      if (res.status === 'applied' || res.status === 'validated') {
        for (const key of res.term_keys) done.add(key);
      }
    }
    if (done.size > 0) setPushedKeys(prev => new Set([...prev, ...done]));
  };

  const summary = result?.summary;

  // Tab counts derived live from the loaded rows (they shift as AI re-scores) and
  // respect the AI filter, so the counts show how many exist for just AI / just Rules.
  const liveCounts = useMemo(() => {
    const c: Record<Section, number> = {
      negatives: 0,
      'refine-match-type': 0,
      'add-keyword': 0,
      'funnel-review': 0,
      monitor: 0,
    };
    const inAiFilter = (r: ReviewRow) =>
      aiFilter === 'all' || (aiFilter === 'ai' ? r.evaluated_by === 'llm' : r.evaluated_by !== 'llm');
    result?.rows.forEach(r => {
      if (inAiFilter(r)) c[r.section] = (c[r.section] ?? 0) + 1;
    });
    // The Add-Keyword tab folds plural variants into one canonical row, so its count is
    // the number of grid rows shown, not the raw term count (matches the tab contents).
    const addKeywordRows = (result?.rows ?? []).filter(r => r.section === 'add-keyword' && inAiFilter(r));
    c['add-keyword'] = groupPlurals(addKeywordRows).gridRows.length;
    return c;
  }, [result, aiFilter]);

  // The lazy-loaded monitor tail is entirely deterministic (Rules), so it only counts
  // when the AI filter isn't "AI only".
  const monitorTail = aiFilter === 'ai' ? 0 : result?.monitorCount ?? 0;
  const monitorTotal = monitorTail + liveCounts.monitor;
  const tabCount = (section: Section) =>
    section === 'monitor' ? monitorTotal : liveCounts[section];

  const tabRows = useMemo(() => {
    if (!result) return [];
    if (activeTab === 'monitor') {
      return [...result.rows.filter(r => r.section === 'monitor'), ...monitorRows];
    }
    return result.rows.filter(r => r.section === activeTab);
  }, [activeTab, monitorRows, result]);
  const monitorRemaining = (result?.monitorCount ?? 0) - monitorRows.length;

  // Client-side filters applied to the active tab's rows.
  const filteredRows = useMemo(() => {
    const camps = filterCampaigns.map(c => c.trim().toLowerCase()).filter(Boolean);
    const numOrNull = (s: string) => (s.trim() === '' || Number.isNaN(Number(s)) ? null : Number(s));
    const minS = numOrNull(filterMinSpend);
    const maxS = numOrNull(filterMaxSpend);
    const minR = numOrNull(filterMinRoas);
    const maxR = numOrNull(filterMaxRoas);
    return tabRows.filter(r => {
      const name = (r.campaign_name || '').toLowerCase();
      if (camps.length > 0 && !camps.some(c => name.includes(c))) return false;
      if (minS != null && r.spend < minS) return false;
      if (maxS != null && r.spend > maxS) return false;
      if (minR != null && (r.roas == null || r.roas < minR)) return false;
      if (maxR != null && (r.roas == null || r.roas > maxR)) return false;
      if (aiFilter === 'ai' && r.evaluated_by !== 'llm') return false;
      if (aiFilter === 'rules' && r.evaluated_by === 'llm') return false;
      if (!flagMatches(r, flagFilter)) return false;
      return true;
    });
  }, [tabRows, filterCampaigns, filterMinSpend, filterMaxSpend, filterMinRoas, filterMaxRoas, aiFilter, flagFilter]);

  // Distinct campaign names across the loaded rows — options for the campaign filter.
  const campaignOptions = useMemo(() => {
    const set = new Set<string>();
    (result?.rows ?? []).forEach(r => r.campaign_name && set.add(r.campaign_name));
    monitorRows.forEach(r => r.campaign_name && set.add(r.campaign_name));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [result, monitorRows]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Search Term Review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Dosaze SQR audit — recommend now, apply later. No Google Ads changes are made;
        approve/deny is saved as a shared audit trail and approved rows can be exported.
      </Typography>

      {/* Scope controls — top-aligned so fields with helper text (Campaigns, Your name)
          keep their INPUT boxes level with the fields that have none, instead of being
          pushed up by the helper line. */}
      <RoundedPaper sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'flex-start' }}
        >
          <TextField
            select
            label="Market"
            value={store}
            onChange={e => {
              // Switching markets invalidates the current campaign selection (its
              // options belong to the old market).
              setStore(e.target.value);
              setSelectedCampaigns([]);
            }}
            sx={{ minWidth: 110 }}
            size="small"
          >
            {STORES.map(s => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <Autocomplete
            multiple
            size="small"
            options={campaigns}
            getOptionLabel={c => (typeof c === 'string' ? c : c.name)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={selectedCampaigns}
            onChange={(_, v) => setSelectedCampaigns(v as Campaign[])}
            loading={campaignsLoading}
            limitTags={2}
            disableCloseOnSelect
            sx={{ minWidth: 320, maxWidth: 520 }}
            renderInput={params => (
              <TextField
                {...params}
                label="Campaigns (optional)"
                placeholder={selectedCampaigns.length ? '' : 'All campaigns in market'}
                helperText={
                  campaignsLoading
                    ? 'Loading campaigns…'
                    : selectedCampaigns.length
                    ? `Auditing ${selectedCampaigns.length} selected campaign(s)`
                    : `Pick from ${campaigns.length} campaign(s), or leave empty for all`
                }
              />
            )}
          />
          <TextField
            label="Lookback (days)"
            type="number"
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            size="small"
            sx={{ width: 130 }}
          />
          <TextField
            label="Min spend ($)"
            type="number"
            value={spendFloor}
            onChange={e => setSpendFloor(Number(e.target.value))}
            size="small"
            sx={{ width: 130 }}
          />
          <Tooltip title="End the window this many days before today. Google attributes conversions to the CLICK date retroactively (a Friday click can convert Sunday), so the most recent days always undercount conversions until later syncs restate them — zero-conversion terms there aren't proven waste yet. 0 audits right up to yesterday.">
            <TextField
              label="Lag buffer (days)"
              type="number"
              value={lagDays}
              onChange={e => setLagDays(Number(e.target.value))}
              size="small"
              inputProps={{ min: 0, max: 14 }}
              sx={{ width: 130 }}
            />
          </Tooltip>
          <Button
            variant="contained"
            onClick={runAudit}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ minWidth: 130 }}
          >
            {loading ? 'Running…' : 'Run audit'}
          </Button>
          <TextField
            select
            label="Audit history"
            value=""
            onChange={e => e.target.value && loadRun(e.target.value)}
            size="small"
            disabled={runs.length === 0 || loadingRunId != null}
            helperText={loadingRunId ? 'Loading saved audit…' : ' '}
            sx={{ minWidth: 210 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="" disabled>
              {runs.length === 0 ? 'No saved audits yet' : `Reopen a past audit (${runs.length})`}
            </MenuItem>
            {runs.map(r => (
              <MenuItem key={r.run_id} value={r.run_id}>
                {formatRunTime(r.created_at)}
                {' — '}
                {r.store} · {r.days}d{r.keyword ? ` · “${r.keyword}”` : ''} · {r.terms_reviewed.toLocaleString()} terms
              </MenuItem>
            ))}
          </TextField>
          <Box sx={{ flexGrow: 1 }} />
          <TextField
            label="Your name (for the audit trail)"
            value={reviewerName}
            onChange={e => onReviewerChange(e.target.value)}
            size="small"
            error={reviewerName.trim() === ''}
            helperText={reviewerName.trim() === '' ? 'Required to approve/deny' : ' '}
            sx={{ minWidth: 220 }}
          />
        </Stack>
      </RoundedPaper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          Audit failed: {error}
        </Alert>
      )}

      {/* AI settings (persisted in the shared dashboard settings store) */}
      <RoundedPaper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Review settings
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Saved for everyone. Every actionable term is AI intent-reviewed automatically.
            </Typography>
          </Box>
          <Tooltip title="Profitability is judged against each campaign/ad group's OWN bid target (negative-eligible below 0.5× tROAS, or above 2× tCPA). This value is only the FALLBACK for placements with no declared target. True waste additionally requires an AI intent mismatch.">
            <TextField
              label="Fallback ROAS threshold"
              type="number"
              value={negRoasThreshold}
              onChange={e => setNegRoasThreshold(Number(e.target.value))}
              size="small"
              inputProps={{ min: 0, step: 0.1 }}
              sx={{ width: 140 }}
            />
          </Tooltip>
          <Tooltip title="Makes each row's Google Ads link open the right account. Open ads.google.com in the Dosaze account and copy the number after ocid= from the address bar. Leave blank to open your last-used account.">
            <TextField
              label="Google Ads ocid"
              value={googleAdsOcid}
              onChange={e => setGoogleAdsOcid(e.target.value)}
              size="small"
              placeholder="e.g. 123456789"
              sx={{ width: 170 }}
            />
          </Tooltip>
          <Button variant="outlined" onClick={saveSettings} disabled={savingSettings} sx={{ minWidth: 130 }}>
            {savingSettings ? 'Saving…' : 'Save settings'}
          </Button>
          {settingsSaved && (
            <Typography variant="caption" color="success.main" sx={{ alignSelf: 'center' }}>
              Saved — applies on the next run.
            </Typography>
          )}
        </Stack>
      </RoundedPaper>

      {/* Run-level warnings (the executive summary block was removed on purpose —
          only the actionable alerts survive) */}
      {summary && (
        <Stack spacing={1} sx={{ mb: 2 }}>
            {summary.dataQuality.map(msg => (
              <Alert key={msg} severity="warning">
                {msg}
              </Alert>
            ))}
            {aiFailedRows.length > 0 && (
              <Alert
                severity="warning"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      const rows = aiFailedRows;
                      setAiFailedRows([]);
                      scoreAiPending(rows, result?.header.runId);
                    }}
                  >
                    Retry scoring ({aiFailedRows.length})
                  </Button>
                }
              >
                {aiFailedRows.length} term(s) couldn’t be intent-scored (the scoring requests failed twice) —
                they’re unclassified, not judged. Retry to score just those terms.
              </Alert>
            )}
            {fallbackRows.length > 0 && (
              <Alert
                severity="warning"
                action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={retryAi}
                  disabled={retrying}
                  startIcon={retrying ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                  {retrying ? 'Retrying…' : `Retry AI (${fallbackRows.length})`}
                </Button>
              }
            >
                {fallbackRows.length} term(s) fell back to basic rules — the AI pass didn’t
                return a result. Retry to re-run AI on just those terms.
              </Alert>
            )}
        </Stack>
      )}

      {/* AI-scoring progress */}
      {aiProgress && (
        <RoundedPaper sx={{ p: 2, mb: 2 }}>
          {(() => {
            const done = aiProgress.done >= aiProgress.total;
            const pct = Math.round((aiProgress.done / Math.max(1, aiProgress.total)) * 100);
            return (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {done ? 'AI scoring complete' : 'AI-scoring top-spend terms'} — {aiProgress.done} /{' '}
                    {aiProgress.total}
                  </Typography>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography variant="body2" color={done ? 'success.main' : 'text.secondary'}>
                      {pct}%
                    </Typography>
                    {done && (
                      <Button size="small" onClick={() => setAiProgress(null)}>
                        Hide
                      </Button>
                    )}
                  </Stack>
                </Stack>
                <FlowingProgress value={pct} animate={!done} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Across {aiProgress.campaigns.length} campaign(s):{' '}
                  {aiProgress.campaigns.slice(0, 3).join(', ')}
                  {aiProgress.campaigns.length > 3 ? ` +${aiProgress.campaigns.length - 3} more` : ''}
                </Typography>
              </>
            );
          })()}
        </RoundedPaper>
      )}

      {/* Sections + table */}
      {result && (
        <RoundedPaper sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              sx={{ flexGrow: 1, minWidth: 0 }}
            >
              {SECTION_ORDER.map(section => (
                <Tab
                  key={section}
                  value={section}
                  label={`${SECTION_LABELS[section]} (${tabCount(section)})`}
                />
              ))}
            </Tabs>
            <Tooltip
              title={
                reviewerName.trim() === ''
                  ? 'Enter your name (top right) to push — the push is recorded against the audit trail.'
                  : pushableRows.length === 0
                  ? 'Approve rows in Negatives or Add Keyword. Refine Match Type changes are applied by hand from the CSV export.'
                  : 'Applies every approved change to the Google Ads account after confirmation.'
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={pushing ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
                  disabled={pushableRows.length === 0 || pushing || reviewerName.trim() === ''}
                  onClick={() => setPushConfirmOpen(true)}
                  sx={{ ml: 2, whiteSpace: 'nowrap' }}
                >
                  {pushing ? 'Pushing…' : `Push to Google Ads (${pushableRows.length})`}
                </Button>
              </span>
            </Tooltip>
          </Stack>

          {/* Client-side table filters */}
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 1.5, gap: 1 }}>
            <Autocomplete
              multiple
              freeSolo
              size="small"
              options={campaignOptions}
              value={filterCampaigns}
              onChange={(_, v) => setFilterCampaigns(v as string[])}
              sx={{ minWidth: 260, maxWidth: 460 }}
              renderInput={params => (
                <TextField {...params} label="Campaign contains (any)" placeholder={filterCampaigns.length ? '' : 'All campaigns'} />
              )}
            />
            <TextField
              label="Min spend"
              type="number"
              value={filterMinSpend}
              onChange={e => setFilterMinSpend(e.target.value)}
              size="small"
              sx={{ width: 110 }}
            />
            <TextField
              label="Max spend"
              type="number"
              value={filterMaxSpend}
              onChange={e => setFilterMaxSpend(e.target.value)}
              size="small"
              sx={{ width: 110 }}
            />
            <TextField
              label="Min ROAS"
              type="number"
              value={filterMinRoas}
              onChange={e => setFilterMinRoas(e.target.value)}
              size="small"
              sx={{ width: 100 }}
            />
            <TextField
              label="Max ROAS"
              type="number"
              value={filterMaxRoas}
              onChange={e => setFilterMaxRoas(e.target.value)}
              size="small"
              sx={{ width: 100 }}
            />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={aiFilter}
              onChange={(_, v) => v && setAiFilter(v)}
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="ai">AI only</ToggleButton>
              <ToggleButton value="rules">Rules only</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={flagFilter}
              onChange={(_, v) => v && setFlagFilter(v)}
            >
              {(Object.keys(FLAG_LABELS) as FlagFilter[]).map(f => (
                <ToggleButton key={f} value={f}>
                  {FLAG_LABELS[f]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {filteredRows.length} of {tabRows.length} shown
            </Typography>
          </Stack>

          {filteredRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {tabRows.length === 0 ? 'No terms in this section.' : 'No terms match the filters.'}
            </Typography>
          ) : (
            <SearchTermTable
              rows={filteredRows}
              section={activeTab}
              reviewerName={reviewerName}
              savingKey={savingKey}
              bulkSaving={bulkSaving}
              googleAdsOcid={googleAdsOcid}
              pushedKeys={pushedKeys}
              onDecision={handleDecision}
              onBulkDecision={handleBulkDecision}
            />
          )}
          {activeTab === 'monitor' && monitorRemaining > 0 && (
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button onClick={loadMoreMonitor} disabled={monitorLoading}>
                {monitorLoading
                  ? 'Loading…'
                  : `Load ${Math.min(result.monitorPageSize, monitorRemaining)} more monitor terms (${monitorRemaining} left)`}
              </Button>
            </Box>
          )}
        </RoundedPaper>
      )}

      {loading && (
        <Typography variant="body2" color="text.secondary">
          Running audit…
        </Typography>
      )}
      {!result && !loading && (
        <Typography variant="body2" color="text.secondary">
          Choose a market and run the audit to review unactioned search terms.
        </Typography>
      )}

      {/* Push confirmation — states exactly how many changes will be made. */}
      <Dialog open={pushConfirmOpen} onClose={() => setPushConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Push {pushableRows.length} approved change(s) to Google Ads?</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            {(['negatives', 'add-keyword'] as Section[]).map(sec => {
              const n = pushableRows.filter(r => r.section === sec).length;
              return n > 0 ? (
                <Typography key={sec} variant="body2">
                  • {SECTION_LABELS[sec]}: <strong>{n}</strong> approved row(s)
                </Typography>
              ) : null;
            })}
            <Typography variant="body2" color="text.secondary">
              This will create the negatives / keywords in the live Google Ads account. Each change is
              validated first, applied, then verified against the account. Nothing is ever paused or removed.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPushConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<CloudUploadIcon />} onClick={doPush}>
            Yes — push {pushableRows.length} change(s)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Push results — the confirmation summary of everything that was made. */}
      <Dialog open={pushResponse != null} onClose={() => setPushResponse(null)} maxWidth="md" fullWidth>
        {pushResponse && (
          <>
            <DialogTitle>
              {pushResponse.mode === 'validate-only'
                ? 'Validation complete — no changes applied (non-production environment)'
                : 'Push complete'}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={1.5}>
                {pushResponse.mode === 'validate-only' && (
                  <Alert severity="info">
                    This environment runs Google Ads in validate-only mode: every change below passed full API
                    validation, but nothing was written. In production the same click applies them.
                  </Alert>
                )}
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {pushResponse.results.filter(r => r.status === 'applied').length} applied ·{' '}
                  {pushResponse.results.filter(r => r.status === 'validated').length} validated ·{' '}
                  {pushResponse.results.filter(r => r.status === 'failed').length} failed
                  {pushResponse.rejected.length > 0 ? ` · ${pushResponse.rejected.length} rejected` : ''}
                </Typography>
                <Divider />
                {pushResponse.results.map((r, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="flex-start">
                    <Chip
                      size="small"
                      color={r.status === 'failed' ? 'error' : r.status === 'applied' ? 'success' : 'info'}
                      label={r.status}
                      sx={{ mt: 0.2, minWidth: 76 }}
                    />
                    <Stack sx={{ minWidth: 0 }}>
                      <Typography variant="body2">{r.description}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.campaign_name}
                        {r.ad_group_name ? ` › ${r.ad_group_name}` : ''} · from “{r.search_terms.join('”, “')}”
                        {r.status === 'applied' && r.verified === false ? ' · verification inconclusive — check in Google Ads' : ''}
                      </Typography>
                      {r.error && (
                        <Typography variant="caption" color="error">
                          {r.error}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                ))}
                {pushResponse.rejected.map(r => (
                  <Typography key={r.term_key} variant="caption" color="error">
                    Rejected: “{r.search_term}” ({r.reason})
                  </Typography>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={() => setPushResponse(null)}>
                Done
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
